import { apiDelete, apiGet, apiPatch, apiPost } from "../fetch";
import { AssignmentStatus, AssignmentType } from "../generated/django-enums";
import { BoardConfigType } from "./board-types";

export { AssignmentStatus, AssignmentType };

export interface StatusChoice {
	value: number;
	key: string;
	name: string;
	description: string;
}

// Theme key type for assignment status colors
export type AssignmentStatusThemeKey =
	| "incoming"
	| "preparation"
	| "active"
	| "paused"
	| "sentToClient"
	| "toBeClosed"
	| "doneSuccess"
	| "doneFailed"
	| "inactive";

// Maps AssignmentStatus enum member names to theme.assignmentStatus property keys.
// Keys are the Django member names, which is what `StatusChoice.key` and the
// reverse lookup `AssignmentStatus[status]` both yield.
export const statusToThemeKey: Record<string, AssignmentStatusThemeKey> = {
	INCOMING: "incoming",
	PREPARATION: "preparation",
	ACTIVE: "active",
	PAUSED: "paused",
	SENT_TO_CLIENT: "sentToClient",
	TO_BE_CLOSED: "toBeClosed",
	DONE_SUCCESS: "doneSuccess",
	DONE_FAILED: "doneFailed",
	INACTIVE: "inactive",
};

// Enum member names, i.e. what `StatusChoice.key`, the reverse lookup
// `AssignmentStatus[status]` and the `statusFilter` URL param all carry.
export type AssignmentStatusKey = keyof typeof AssignmentStatus;

// Statuses preselected when no `statusFilter` is present in the URL.
// Typed so a renamed enum member fails the build instead of silently
// resolving to nothing — which widens the query to every assignment.
export const DEFAULT_ASSIGNMENT_STATUS_KEYS: AssignmentStatusKey[] = [
	"ACTIVE",
	"INCOMING",
	"PREPARATION",
	"SENT_TO_CLIENT",
	"TO_BE_CLOSED",
];

export const DEFAULT_ASSIGNMENT_STATUS_FILTER =
	DEFAULT_ASSIGNMENT_STATUS_KEYS.join(",");

/** Resolves status keys (URL param or defaults) to their enum values. */
export const statusKeysToValues = (keys: string[]): AssignmentStatus[] =>
	keys
		.map((key) => {
			const value = AssignmentStatus[key as AssignmentStatusKey];
			if (value === undefined)
				console.warn(`Unknown assignment status key '${key}' in status filter`);
			return value;
		})
		.filter((value): value is AssignmentStatus => value !== undefined);

/** A role's own public ad, as the role lists need it: where it lives and
 *  whether it is live yet. The full ad is served by /api/v2/job-ads/. */
export type AssignmentJobAd = {
	url: string;
	is_published: boolean;
};

export type Assignment = {
	id?: number;
	name: string;
	description: string;
	mandatory_keywords?: string;
	search_query_text?: string;
	status?: AssignmentStatus;
	status_display?: string;
	company?: number;
	company_name: string;
	assignment_type?: AssignmentType;
	created: string;
	users: number[];
	use_email?: boolean;
	use_inmail?: boolean;
	use_invite?: boolean;
	use_invite_note?: boolean;
	wait_for_invite?: boolean;
	egenkonsult?: boolean;
	outreach_quiet_period?: boolean;
	only_phone?: boolean;
	allow_rocketreach?: boolean;
	priority?: number;
	config?: BoardConfigType;
	only_corp_email?: boolean;
	add_board_on_up?: boolean;
	show_jobpostings?: boolean;
	board_on_up?: number;
	column_on_up?: string;
	status_on_up?: string;
	active_on_weekends?: boolean;
	ignore_customer_employees?: boolean;
	call_everyone?: boolean;
	search_locations?: number[];
	keywords?: number[];
	preferred_companies?: number[];
	/** Current employees of these companies are excluded from sourcing. */
	excluded_companies?: number[];
	is_recruiting_company: boolean;
	thumbers?: number[];
	/** The client's own posting, typed in by the recruiter. Never our own ad —
	 *  that is `job_ad`. */
	external_url?: string;
	/** This role's own ad on our job board, or null when nobody wrote one. */
	job_ad?: AssignmentJobAd | null;
	work_environment?: string;
	price?: string;
	full_time_percentage?: number;
	comment?: string;
	end_client?: number;
	end_client_name?: string;
	submission_deadline?: string;
	start_date?: string;
	end_date?: string;
	votes?: number;
	llm_candidate_freshness?: string; // ISO 8601 duration e.g. "P30D" for 30 days
	has_manual_requirements?: boolean;
	manual_requirements?: { text: string; mandatory: boolean }[] | null;
	// Optional server-provided aggregation when requested via include_votes_by_user
	votes_by_user?: Record<
		string,
		{
			group: string;
			grades: Record<string, number>;
			cv_import?: number;
		}
	> | null;
	// New per-assignment total experience bounds
	min_total_experience_years?: number | null;
	max_total_experience_years?: number | null;
	// Bounds on the whole stay at a current company (months, inclusive)
	min_months_at_current_company?: number | null;
	max_months_at_current_company?: number | null;
	// Number of users in this assignment's user group
	group_user_count?: number;
	// Language filters - list of LCID integers (AND logic)
	language_filters?: number[];
};

export const getAssignments = (
	sort?: string,
	options?: {
		includeVotesByUser?: boolean;
		statusValues?: number[];
		userId?: number;
		creatorInMyUpGroup?: boolean;
	}
): Promise<Assignment[]> => {
	let url = `/api/v2/assignment/`;
	const params: string[] = [];
	if (sort) params.push(`ordering=${encodeURIComponent(sort)}`);
	if (options?.includeVotesByUser) params.push(`include_votes_by_user=true`);
	if (options?.statusValues?.length)
		params.push(`status__in=${options.statusValues.join(",")}`);
	if (options?.userId) params.push(`users=${options.userId}`);
	if (options?.creatorInMyUpGroup) params.push(`creator_in_my_up_group=true`);
	if (params.length) url += `?${params.join("&")}`;

	return apiGet(url).then((res) => res as Assignment[]);
};

export const getAssignmentById = (
	id: number,
	options?: { includeVotesByUser?: boolean }
): Promise<Assignment> => {
	if (!id || typeof id !== "number") {
		throw new Error(
			`getAssignmentById called with invalid argument id '${id}'. Expected number`
		);
	}

	const params: string[] = [];
	if (options?.includeVotesByUser) params.push(`include_votes_by_user=true`);
	const query = params.length ? `?${params.join("&")}` : "";

	return apiGet(`/api/v2/assignment/${id}/${query}`) as Promise<Assignment>;
};

export type CreateAssignmentArgs = Partial<Assignment>;

export const createAssignment = (
	assignment: CreateAssignmentArgs
): Promise<Assignment> => {
	return apiPost(`/api/v2/assignment/`, assignment) as Promise<Assignment>;
};

export const cloneAssignment = (assignment: Assignment): Promise<Assignment> => {
	// Use the new backend clone endpoint that handles all related data
	return apiPost(`/api/v2/assignment/${assignment.id}/clone/`, {
		name: assignment.name, // Pass the new name with unique suffix
	}) as Promise<Assignment>;
};

export const patchAssignmentById = (
	id: number,
	values: Partial<Assignment>
): Promise<Assignment> => {
	if (!id || typeof id !== "number") {
		throw new Error(
			`getAssignmentById called with invalid argument id '${id}'. Expected number`
		);
	}

	return apiPatch(`/api/v2/assignment/${id}/`, { ...values }) as Promise<Assignment>;
};

export const deleteAssignment = (id: number): Promise<any> => {
	if (!id || typeof id !== "number") {
		throw new Error(
			`deleteAssignment called with invalid argument id '${id}'. Expected number`
		);
	}

	return apiDelete(`/api/v2/assignment/${id}/`).then((res) => {
		console.log(res);
		return res;
	});
};

export interface AssignmentLLMSkills {
	id: number;
	name: string;
	description: string;
	llm_prompt: string;
	llm_response: string;
}

export const getAssignmentLLMSkills = async (): Promise<AssignmentLLMSkills[]> => {
	const data = await apiGet("/api/v2/assignment_llm_skills/");
	return data as AssignmentLLMSkills[];
};

export interface AvailableLanguage {
	lcid: number;
	code: string;
	name: string;
	locale: string;
	country_code: string;
	mainstream: boolean;
}

/** The language list is fixed for a deployed build, so one request serves everyone. */
let pendingAvailableLanguages: Promise<AvailableLanguage[]> | null = null;

/**
 * Three components fetch this independently — the language picker, the
 * invitation-note editor and the role wizard — and opening a role used to send
 * the request two to four times in the same load.
 */
export const getAvailableLanguages = (): Promise<AvailableLanguage[]> => {
	if (!pendingAvailableLanguages) {
		pendingAvailableLanguages = (
			apiGet("/api/v2/assignment/available_languages/") as Promise<
				AvailableLanguage[]
			>
		).catch((error: unknown) => {
			// Keep a failed load retryable rather than caching the rejection.
			pendingAvailableLanguages = null;
			throw error;
		});
	}
	return pendingAvailableLanguages;
};

export interface CountryLanguage {
	country_code: string;
	code: string;
	lcid: number;
}

export const getLanguagesForCountries = async (
	countryCodes: string[]
): Promise<CountryLanguage[]> => {
	const data = await apiPost("/api/v2/assignment/languages_for_countries/", {
		country_codes: countryCodes,
	});
	return data as CountryLanguage[];
};

export interface RandomDescriptionResponse {
	description: string;
}

export const getRandomDescription = async (): Promise<RandomDescriptionResponse> => {
	const data = await apiGet("/api/v2/assignment/random_description/");
	return data as RandomDescriptionResponse;
};

export const rebuildAssignment = (id: number): Promise<{ status: string }> => {
	return apiPost(`/api/v2/assignment/${id}/rebuild/`, {}) as Promise<{
		status: string;
	}>;
};

export const rebuildAssignmentMode = (
	id: number,
	mode: string
): Promise<{ status: string }> => {
	return apiPost(`/api/v2/assignment/${id}/rebuild_mode/`, {
		mode,
	}) as Promise<{ status: string }>;
};

export const getStatusChoices = async (): Promise<StatusChoice[]> => {
	const data = await apiGet("/api/v2/assignment/status_choices/");
	return data as StatusChoice[];
};
